import { useEffect, useState } from 'react';
import { z } from 'zod';
import styled, { css } from 'styled-components';
import {
  ButtonContainer,
  InputCheck,
  InputWrapper,
  Loading,
  PrimaryButton,
  SecondaryButton,
  PrimaryAltButton,
  SectionWrapper
} from '../../atoms';
import { APIEndPoints } from '../../../apis';
import { zodResolver } from '@hookform/resolvers/zod';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { Request } from '../../../apis';
import { toast } from 'react-hot-toast';

type TSetUserPassword = {
  email: string;
  isModalOpen: boolean;
};

const ToggleButton = styled((props) => <PrimaryAltButton {...props} />)`
  margin-bottom: 15px;
`;

const ErrorStyles = styled.div(
  ({ theme }) => css`
    text-align: right;
    color: ${theme.colors.main.error};
    margin-bottom: 15px;
  `
);

const FormFooter = styled((props) => <ButtonContainer {...props} />)`
  margin-top: 5px;
  margin-bottom: 20px;
`;

const setUserPasswordSchema = z.object({
  newPassword: z
    .string()
    .regex(/^.{8,}$/, 'Your password must be at least 8 characters')
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])/,
      'Your password must have at least 1 uppercase and lowercase character'
    )
    .regex(/^(?=.*[0-9])/, 'Your password must have at least 1 number')
    .regex(
      /^(?=.*[!@#$%^&*])/,
      'Your password must have at least 1 special character'
    ),
  permanent: z.boolean().optional()
});

type SetUserPasswordSchema = z.infer<typeof setUserPasswordSchema>;

const SetUserPassword = ({ email, isModalOpen }: TSetUserPassword) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<SetUserPasswordSchema>({
    resolver: zodResolver(setUserPasswordSchema)
  });

  const requestSchema = z.object({
    id: z.string().optional(),
    status: z.number().optional(),
    message: z.string()
  });

  const [errorMessage, setErrorMessage] = useState('');
  const [processing, setProcessing] = useState<boolean>(false);
  const [isPromptOpen, togglePromptOpen] = useState(false);

  const handleReset = () => {
    setProcessing(false);
    setErrorMessage('');
    // reset form
    reset();
  };

  useEffect(() => {
    if (isModalOpen === false) {
      handleReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);

  const onSubmit: SubmitHandler<SetUserPasswordSchema> = async (data) => {
    setErrorMessage('');
    try {
      const { newPassword, permanent } = data;

      setProcessing(true);
      const response = await Request.post(
        `${APIEndPoints.adminSetUserPassword}`,
        requestSchema,
        {
          permanent: `${permanent}`,
          credentials: newPassword,
          username: email
        }
      );

      if (response.status === 200) {
        try {
          const response = await Request.post(
            `${APIEndPoints.sendEmail}`,
            requestSchema,
            {
              text: `Your new password is ${newPassword}`,
              subject: 'New password set',
              from: 'noreply@nimble.com.au',
              to: email
            }
          );

          if (response.status === 202) {
            setProcessing(false);

            toast.success('New password set and emailed to user.');
            togglePromptOpen(false);
          } else {
            setErrorMessage('Failed to send email');
            setProcessing(false);
          }
        } catch {
          setErrorMessage('Failed to send email'); // generic error
          setProcessing(false);
        }
      } else {
        setErrorMessage('Something unexpected occurred.');
        setProcessing(false);
      }
    } catch {
      setErrorMessage('Something unexpected occurred.'); // generic error
      setProcessing(false);
    }
  };

  return (
    <SectionWrapper>
      <ToggleButton
        type="button"
        disabled={isPromptOpen}
        onClick={() => togglePromptOpen(true)}
      >
        Set new password
      </ToggleButton>
      {isPromptOpen && (
        <form onSubmit={handleSubmit(onSubmit)} noValidate={true}>
          <InputWrapper
            name="newPassword"
            type="text"
            label="New Password"
            register={register}
            errorMessage={errors.newPassword?.message}
          />
          <InputCheck
            name="permanent"
            label="Permanent"
            required
            register={register}
            errorMessage={errors.permanent?.message}
          />
          <ErrorStyles>{errorMessage}</ErrorStyles>

          <FormFooter
            horizontalSpacing="10px"
            verticalSpacing="0px"
            justify="end"
          >
            {processing ? (
              <Loading />
            ) : (
              <>
                <SecondaryButton
                  type="button"
                  onClick={() => togglePromptOpen(false)}
                >
                  Cancel
                </SecondaryButton>
                <PrimaryButton type="submit">
                  Set Password and send email
                </PrimaryButton>
              </>
            )}
          </FormFooter>
        </form>
      )}
    </SectionWrapper>
  );
};

export { SetUserPassword };
